import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import {
  AccountEntities,
  EntityTypeID,
  ErrorTypes,
  FeatureFlags,
  FieldListObject,
  getDatesFromDefinition,
  getOperator,
  isDateRangeEmpty,
  isDateRangeNotEmpty,
  isNullOperator,
  SeverityLabels,
  useStaticErrorSnackBar,
  WorkflowType,
} from '@fabric-ui/components';
import { emptyArray, emptyObject, getIfValidStringifiedObject, noop, QueryKey } from '@fabric-ui/utils';
import { generateSourcesValuesQuery } from '@shared/Tickets/utils';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { ACTIONS_MAP, EntityConfigType, entityViewConfig, getActionsPermissionConfig } from '@utils/entityViewConfig';
import { and, field } from '@utils/filter';
import { projectionNameToCategoryName } from '@utils/permissions.utils';
import {
  arrayToCsv,
  downloadBlob,
  getEscapedValue,
  getEscapedValueForSQLLike,
  getFieldName,
  getNetworkErrorCode,
  sqlFormatter,
} from '@utils/utils';
import { Outegration, TARGET_MATCHING_CATEGORY_ENUM, TARGET_MATCHING_TYPE_ENUM } from '@views/Outegrations/types';
import { add, sub } from 'date-fns';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { ScreenType } from 'src/types/savedViews.types';
import { useAvContext } from '../../context/AvContextProvider';
import { MessageStatus, NotificationContext } from '../../context/AvSnackBarProvider';
import useHandleError from '../../hooks/useHandleError';
import useQueryObject from '../../hooks/useQueryObjectSql';
import useQuerySql from '../../hooks/useQuerySql';
import { Permission, PermissionAppName, PermissionCategoriesNames, PermissionEntitiesNames } from '../../types';
import { DwQueryRequest, QueryObject } from '../CustomDashboards/types';
import { getIsManualOverrideEnabled, useFieldRegulationsData } from '../ModelManagement/hooks';
import { useGetBucketAndStatuses } from '../Settings/TicketStatuses/hooks';
import { TabTypeEnum, TTab } from '../Settings/UiConfigSettings/types';
import { FieldType, FieldTypeEnum } from '../Sources/Mapping/types';
import useIsThreatsApp from './Threats/useIsThreatsApp';
import {
  apiUrls,
  filtersFieldToIgnore,
  formatAsUTC,
  getBaseEntitySqlsMap,
  groupByKeys,
  groupByKeysQueryProjection,
  TABLE_AVALOR_KEY,
} from './ticket.types';
import { ConfigField, getFieldId, metricsPerProjName } from './ticketInfo.utils';

export const useConfirmedAndRemediatedBuckets = proj => {
  const { buckets } = useGetBucketAndStatuses(proj);
  return useMemo(() => {
    const confirmedBucket = buckets.find(b => b.closed) || { statuses: [] };
    const remediatedBucket = buckets.find(b => b.remediated) || { statuses: [] };

    return { confirmedBucket, remediatedBucket };
  }, [buckets]);
};

interface CreateDestinationIssueForTicketOptions {
  entityKey: string | undefined | number;
  projectionId: EntityTypeID;
  onSuccess?: (res: any) => void;
}

interface ActionStatus {
  mode: 'loading' | 'success' | 'error';
  message?: string;
  timedOut?: boolean;
}

export interface IOutegration {
  id: string;
  name: string;
  active: boolean;
  integrationType: string;
}

export type CreateIssueParams = {
  integrationId: string;
  integrationKey?: string;
  dependency?: Promise<any>;
  destinationName: string;
};

export type IntegrationSingleTM = {
  entityKeyToIntegrationId: EntityKeyToIntegrationId[];
  projectionId: EntityTypeID;
};

export type EntityKeyToIntegrationId = {
  entityKey: string;
  integrationId: string;
};

export const useCreateDestinationIssueForTicket = ({
  entityKey,
  projectionId,
  onSuccess = noop,
}: CreateDestinationIssueForTicketOptions) => {
  const { api } = useAvContext();
  const staticErrorSnackBar = useStaticErrorSnackBar();
  const { setRunInfo } = useContext(NotificationContext);

  const [actionStatus, setActionStatus] = useState<Partial<ActionStatus>>({});

  const createIssue = useCallback(
    ({ integrationId, integrationKey, dependency = Promise.resolve(), destinationName }: CreateIssueParams) => {
      setActionStatus({ mode: 'loading' });
      const requestUrl = integrationKey ? `${apiUrls.CREATE_SINGLE_INTEGRATION}/manual` : apiUrls.CREATE_SINGLE_INTEGRATION;
      return dependency
        .then(() =>
          api(requestUrl, {
            options: { method: 'POST' },
            onError: () => {},
            isWebserver: false,
            body: { entityKey, integrationId, integrationKey, projectionId },
            onSuccess: (res: any) => {
              onSuccess(res);
              if (res.attachmentWfResponse) {
                setRunInfo({
                  wfId: res.attachmentWfResponse.workflowId,
                  runId: res.attachmentWfResponse.runId,
                  workflowType: WorkflowType.IntegrationAttachment,
                });
              }
              setActionStatus({ mode: 'success' });
            },
          })
        )
        .catch((e: Error) => {
          setActionStatus({ mode: 'error', message: e.message });
          staticErrorSnackBar({ title: `Create ${destinationName} Ticket Failed`, message: e.message });
        })
        .finally(() => setTimeout(() => setActionStatus(prev => ({ ...prev, timedOut: true })), 3000));
    },
    [entityKey, onSuccess]
  );

  return { createIssue, actionStatus };
};

const mapValueFunc = v => {
  const stringValue = `${v}`;
  return `'${getEscapedValue(stringValue)}'`;
};
export const PAGE_SIZE = 20;
export const queryOrderBy = (map: any[], filterArr?: any[], isTableV2?: boolean) => {
  const orderByFields = map
    .filter(({ property }) => !filterArr || filterArr.find(({ id }) => id === property))
    .map(({ property, isAsc }) => `${property} ${isAsc ? 'ASC' : 'DESC'}`)
    .join(', ');
  const keyHeadCell = isTableV2 ? filterArr?.find(({ meta }) => meta?.isKey) : filterArr?.find(({ isKey }) => isKey);
  return map.length ? `ORDER BY ${orderByFields || `${(keyHeadCell || filterArr?.[0])?.id} ASC`}` : '';
};
const ROW_LIMIT = 40000;

const aliasRegex = /(?: as )/gi;

const getDefaultOrderBy = f => {
  const field = (f && f.split(aliasRegex)?.[1]) || f;
  return [{ property: field }];
};

export const useValidateEntitiesExist = ({
  mainAlias,
  enabled,
  entityKeys = emptyArray,
  projectionName,
}: {
  enabled: boolean;
  entityKeys: string[];
  mainAlias: string;
  projectionName: string;
}) => {
  const keyField = `${mainAlias}._key`;
  const queryObject: QueryObject = {
    select: {
      dims: [{ name: keyField }],
      metrics: [],
    },
    filter: and(field(keyField).str.in(entityKeys)),
    projectionId: { name: projectionName, builtIn: true },
    sorting: [],
  };
  const { data = emptyArray, isLoading } = useQueryObject({
    queryObject,
    onSuccess: d => d.map(v => v[keyField]),
    options: { enabled },
  });
  const existingEntities = entityKeys.filter(key => data.includes(key));
  return { existingEntities, isLoading };
};

export const useTicketFieldsQuery = ({
  ticketKey,
  queryKey = 'tab-table',
  where = '',
  page = 0,
  fields = [],
  fieldToExcludeFromExport = [],
  orderBy = getDefaultOrderBy(fields[0]),
  enabled,
  keyPrefix = 'finding',
  groupBy,
  parentEntityPathAlias = 'ticket',
  isCountDistinctValues,
  projectionName,
  isDistinct,
}: {
  ticketKey: string;
  queryKey?: string;
  where?: string;
  page?: number;
  fields?: string[];
  fieldToExcludeFromExport?: string[];
  orderBy?: any[];
  enabled?: boolean;
  keyPrefix?: string;
  groupBy?: string[];
  parentEntityPathAlias?: string;
  isCountDistinctValues?: boolean;
  projectionName: string;
  isDistinct?: boolean;
}) => {
  const {
    measurements,
    userPermissions: { isInternalRole },
  } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  const shouldCountDistinctValues = isCountDistinctValues;
  const fromTable = projectionName;
  const whereSQL = `${where ? `${where} AND` : 'WHERE'} ${parentEntityPathAlias}._key = ${mapValueFunc(ticketKey)}`;

  const metricsIncludedInFields = useMemo(
    () =>
      measurements.visible.reduce((acc: Set<string>, metric: any) => {
        if (fields.includes(metric.systemName)) {
          acc.add(metric.systemName);
        }

        return acc;
      }, new Set()),
    [fields, measurements]
  );

  const fieldsWithoutMetrics = fields.filter((field: string) => !metricsIncludedInFields.has(field));
  const groupByFields = fieldsWithoutMetrics.map(field => getFieldId(field));

  const allSQL = `SELECT ${isDistinct ? 'DISTINCT ' : ''}${[
    ...new Set([
      ...fieldsWithoutMetrics,
      ...Array.from(metricsIncludedInFields).map((systemName: string) => `${systemName} as ${systemName}`),
      ...(groupBy ? [] : [`${keyPrefix}._key as ${TABLE_AVALOR_KEY}`]),
    ]),
  ].join(',')}
  FROM ${fromTable} ${whereSQL}
      GROUP BY ${groupBy ? groupBy.join(', ') : groupByFields.join(', ')}
      ${queryOrderBy([...orderBy, ...(groupBy ? [] : [{ property: TABLE_AVALOR_KEY, isAsc: true }])])}`;

  const exportSql = fieldToExcludeFromExport.length
    ? `select ${isDistinct ? 'DISTINCT ' : ''}${[...new Set([...fields.filter((f: string) => !fieldToExcludeFromExport.includes(f)), `${keyPrefix}._key`])].join(',')}
       FROM ${fromTable} ${whereSQL}
           ${groupBy ? `GROUP BY ${groupBy.join(', ')}` : ''}
           ${queryOrderBy([...orderBy, ...(groupBy ? [] : [{ property: `${keyPrefix}._key`, isAsc: true }])])}`
    : allSQL;

  const sql = `AVALOR PARAM total_row_count = true;  ${allSQL} OFFSET ${page * PAGE_SIZE} LIMIT ${PAGE_SIZE}`;

  const onError = isInternalRole
    ? (e: any) => {
        const error = e?.[0]?.message || e?.message;
        return error && enqueueSnackbar(`${ErrorTypes.Setup}: ${e?.[0]?.message}`, { variant: 'error' });
      }
    : undefined;

  const { data, isLoading, isRefetching, totals, errors } = useQuerySql({
    key: queryKey,
    sql,
    options: { enabled: enabled && !!ticketKey },
    muteErrors: true,
    debounceQueries: false,
    onError,
    showTotals: true,
  });

  const hasNonNullPrefix = 'HAS_NON_NULL_VALUES_';
  const populatedFieldsQuery =
    shouldCountDistinctValues &&
    `SELECT ${fields
      .filter((f: string) => !f.match(aliasRegex))
      .map(
        (f: string) => `MAX(IF(${f} != null, 1, 0)) as "${hasNonNullPrefix}${f}"`
      )} from ${fromTable} WHERE ${parentEntityPathAlias}._key = ${mapValueFunc(ticketKey)}`;

  const { data: distinctValuesByFieldsMap, isLoading: isLoadingCounts } = useQuerySql({
    key: 'tab-table',
    sql: populatedFieldsQuery as string,
    debounceQueries: false,
    onSuccess: (d: any) =>
      Object.keys(d[0]).reduce((acc: any, key: string) => {
        if (key.includes(hasNonNullPrefix)) {
          return { ...acc, [key.replace(hasNonNullPrefix, '')]: d[0][key] };
        }
        return acc;
      }, {}),
    options: { enabled: enabled && !!ticketKey && shouldCountDistinctValues },
  });

  return {
    data,
    isLoading: isLoading || (!data && !errors?.length),
    isLoadingCounts,
    isRefetching,
    sql: `${allSQL} LIMIT ${ROW_LIMIT}`,
    exportSql,
    count: totals,
    whereSQL,
    distinctValuesByFieldsMap,
  };
};

export const useAllOutegrationsListByProjName = ({
  projName,
  selectFunc,
  enabled,
}: {
  projName: string;
  selectFunc: (data: IOutegration[]) => any;
  enabled?: boolean;
}) =>
  useGqlQuery<IOutegration[]>({
    query: ALL_ALERT_DESTINATIONS_LIST_BY_PROJECTION_NAME,
    queryKey: [QueryKey.outegrationsTicketsDropdown, 'All', projName],
    variables: { projectionName: projName },
    enabled: !!projName && enabled,
    select: selectFunc,
  });

export const OutegrationsListByProjectionName = (isWorkManagement, projectionName, enabled = false, isCMDB = false) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();
  const { data, isLoading } = useGqlQuery<Outegration[]>({
    query: ALERT_DESTINATIONS_LIST_BY_PROJECTION_NAME,
    queryKey: [QueryKey.outegrationsTicketsDropdown, projectionName, enabled, isWorkManagement, isCMDB],
    variables: { projectionName },
    enabled:
      enabled &&
      (projectionName === entityViewConfig.Incident.projectionName ||
        hasAllowedPermissionToResource({
          resource: PermissionEntitiesNames.INT,
          category: projectionNameToCategoryName[projectionName] as PermissionCategoriesNames,
          permission: Permission.CREATE,
        }) ||
        hasAllowedPermissionToResource({
          resource: PermissionEntitiesNames.INT,
          category: projectionNameToCategoryName[projectionName] as PermissionCategoriesNames,
          permission: Permission.UPDATE,
        }) ||
        (projectionName === entityViewConfig.Exception.projectionName &&
          hasAllowedPermissionToResource({
            category: PermissionAppName.VULNERABILITIES,
            resource: PermissionEntitiesNames.EXCEPTION,
            permission: Permission.UPDATE,
          })) ||
        (projectionName === entityViewConfig.Exception.projectionName &&
          hasAllowedPermissionToResource({
            category: PermissionAppName.VULNERABILITIES,
            resource: PermissionEntitiesNames.EXCEPTION,
            permission: Permission.CREATE,
          }))),
  });

  const outegrations: Outegration[] = useMemo(
    () =>
      data?.filter(
        ({ category, active, integrationType }) =>
          (isCMDB
            ? category === TARGET_MATCHING_CATEGORY_ENUM.CMDB
            : isWorkManagement
              ? ![TARGET_MATCHING_CATEGORY_ENUM.WORK_MANAGEMENT, TARGET_MATCHING_CATEGORY_ENUM.CMDB].includes(category) ||
                isAnyTargetType(integrationType)
              : [
                  TARGET_MATCHING_CATEGORY_ENUM.WORK_MANAGEMENT,
                  TARGET_MATCHING_CATEGORY_ENUM.EXCEPTIONS,
                  TARGET_MATCHING_CATEGORY_ENUM.SCANNER,
                ].includes(category)) && !!active
      ) || [],
    [data]
  );
  return { outegrations, isLoading };
};

export function isAnyTargetType(integrationType) {
  return integrationType === 'ANYTARGET';
}

export const predefinedSavedViewFilters = ({ buckets, pathAlias, projectionName, userName }) => {
  const isTickets = projectionName === entityViewConfig.Ticket.projectionName;
  const now = new Date();
  const confirmedKey = 'closed';
  const notConfirmedStatuses = buckets?.filter(bucket => !bucket[confirmedKey]).flatMap(({ statuses }) => statuses.map(({ name }) => name));
  const confirmedStatuses = buckets?.filter(bucket => bucket[confirmedKey]).flatMap(({ statuses }) => statuses.map(({ name }) => name));
  return {
    active: {
      [`${pathAlias}.current_status.name`]: notConfirmedStatuses,
      ...(isTickets && { [`${pathAlias}.state`]: ['ACTIVE'] }),
    },
    openTickets: {
      [`${pathAlias}.current_status.name`]: notConfirmedStatuses,
    },
    pendingConfirmation: {
      [`${pathAlias}.current_status.name`]: notConfirmedStatuses,
      [`${pathAlias}.state`]: [isTickets ? 'INACTIVE' : 'FULLY_REMEDIATED'],
    },
    overSla: {
      [`${pathAlias}.sla`]: [
        encodeURIComponent(JSON.stringify({ value: { from: new Date('2020/01/01').toISOString(), to: now.toISOString() } })),
      ],
      [`${pathAlias}.current_status.name`]: notConfirmedStatuses,
    },
    closedTickets: {
      [`${pathAlias}.current_status.name`]: confirmedStatuses,
      ...(isTickets && { [`${pathAlias}.state`]: ['ACTIVE', 'INACTIVE'] }),
    },
    missingAssignee: { [`${pathAlias}.assignee_id`]: [null] },
    allTickets: {},
    ticketsWithNoIntegration: {
      [`${pathAlias}.integration_info.key`]: [null],
    },
    newFindings: {
      [`finding.first_seen`]: [
        encodeURIComponent(JSON.stringify({ value: { from: sub(now, { days: 7 }).toISOString(), to: now.toISOString() } })),
      ],
    },
    approachingSla: {
      [`${pathAlias}.sla`]: [
        encodeURIComponent(
          JSON.stringify({
            value: {
              from: now.toISOString(),
              to: add(now, {
                days: 5,
              }).toISOString(),
            },
          })
        ),
      ],
    },
    activeByEntity: {
      [`${pathAlias}.state`]: ['ACTIVE'],
    },
    activeCriticalAndHigh: {
      [`${pathAlias}.state`]: ['ACTIVE'],
      [`${pathAlias}.severity`]: [SeverityLabels.Critical, SeverityLabels.High],
    },
    coverage: {
      [`${pathAlias}.state`]: ['ACTIVE'],
      [`${pathAlias}.type`]: ['Coverage'],
    },
    CMDBHygiene: {
      [`${pathAlias}.state`]: ['ACTIVE'],
      [`${pathAlias}.type`]: ['CMDB Hygiene'],
    },
    newAndActive: {
      [`${pathAlias}.state`]: ['ACTIVE'],
      [`${pathAlias}.created`]: [
        encodeURIComponent(JSON.stringify({ value: { from: sub(now, { days: 7 }).toISOString(), to: now.toISOString() } })),
      ],
    },
    exceptionsReviewer: {
      'reviewer.name': [userName],
    },
    exceptionsRequester: {
      'requester.name': [userName],
    },
  };
};

export const prefixes = {
  IP: 'IPADDRESS ',
  FIX: 'FIX ',
};

const normalizeNumberOrString = value => (typeof value === 'number' ? value : `'${value}'`);

export const useBuildWhereClause = ({
  filters = {},
  extra = [],
  entityTypeId = '',
  fieldMap = {},
  allowAllFields = false,
  allowMetrics = false,
}: {
  filters: Record<string, any>;
  extra?: string[];
  entityTypeId?: string;
  fieldMap?: Record<string, any>;
  allowAllFields?: boolean;
  allowMetrics?: boolean;
}) => {
  const {
    measurements,
    accountEntities: { fieldMap: allFieldsMap },
  } = useAvContext();

  const metricsAsFilters = measurements?.visible.reduce((acc, { systemName }) => {
    if (filters[systemName]) {
      acc.add(systemName);
    }

    return acc;
  }, new Set());

  const filtered = Object.keys(filters).filter(
    field =>
      filters[field] &&
      filters[field].length > 0 &&
      !filtersFieldToIgnore.includes(field) &&
      (allowAllFields || !!allFieldsMap[field] || (allowMetrics && metricsAsFilters.has(field)))
  );

  const whereParts = [
    ...filtered.map(fieldNoQuote => {
      const fieldValue = allFieldsMap[fieldNoQuote]?.value || fieldNoQuote;
      const field = fieldValue.includes(' ') ? `"${fieldValue}"` : fieldValue;
      const objectFilter = getIfValidStringifiedObject(filters[fieldNoQuote]?.[0]);
      const fieldType = fieldMap[fieldNoQuote]?.type || allFieldsMap[fieldNoQuote]?.type;
      if (objectFilter) {
        if ([FieldType.DateTime, FieldType.Date].includes(fieldType)) {
          const { value, preset } = objectFilter;
          if (preset) {
            const presetDate = getDatesFromDefinition(preset);
            return `${field} >= '${presetDate.from.toISOString()}' AND ${field} <= '${presetDate.to.toISOString()}'`;
          }
          if (isDateRangeEmpty(value) || value === null) {
            return `${field} = null`;
          }
          if (isDateRangeNotEmpty(value)) {
            return `${field} != null`;
          }
          return `${field} >= '${value.from}' AND ${field} <= '${value.to}'`;
        }
        const operator = Object.keys(objectFilter)[0];

        return `(${objectFilter[operator]
          .map(({ value, operator: compoundOperator }) => {
            const prefix = prefixes[fieldType] || '';
            const sqlLikeEscape = getEscapedValueForSQLLike(value);
            const [operator, resolution] = compoundOperator.split('|');
            const fieldWithRepeated = resolution && !isNullOperator(operator) ? `${resolution}(${field})` : field;

            if (operator === 'contains') {
              return `${fieldWithRepeated} LIKE '%${sqlLikeEscape}%'`;
            }
            if (operator === 'notContains') {
              return `${fieldWithRepeated} NOT LIKE '%${sqlLikeEscape}%'`;
            }
            if (operator === 'starts') {
              return `${fieldWithRepeated} LIKE '${sqlLikeEscape}%'`;
            }
            if (operator === 'ends') {
              return `${fieldWithRepeated} LIKE '%${sqlLikeEscape}'`;
            }
            if (operator === 'equals') {
              return `${fieldWithRepeated} = ${prefix}${normalizeNumberOrString(value)}`;
            }
            if (operator === 'notEqual') {
              return `${fieldWithRepeated} != ${prefix}'${value}'`;
            }
            if (getOperator(operator) === 'gt') {
              return `${fieldWithRepeated} > ${prefix}${normalizeNumberOrString(value)}`;
            }
            if (getOperator(operator) === 'gte') {
              return `${fieldWithRepeated} >= ${prefix}${normalizeNumberOrString(value)}`;
            }
            if (getOperator(operator) === 'lt') {
              return `${fieldWithRepeated} < ${prefix}${normalizeNumberOrString(value)}`;
            }
            if (getOperator(operator) === 'lte') {
              return `${fieldWithRepeated} <= ${prefix}${normalizeNumberOrString(value)}`;
            }
            if (getOperator(operator) === 'ipRange') {
              return `${fieldWithRepeated} >= ${prefix}'${value.from}' AND ${fieldWithRepeated} <= ${prefix}'${value.to}'`;
            }
            if (getOperator(operator) === 'notIpRange') {
              return `${fieldWithRepeated} < ${prefix}'${value.from}' OR ${fieldWithRepeated} > ${prefix}'${value.to}'`;
            }
            if (getOperator(operator) === 'cidr') {
              return `${fieldWithRepeated} = SUBNET '${value}'`;
            }
            if (getOperator(operator) === 'notCidr') {
              return `${fieldWithRepeated} != SUBNET '${value}'`;
            }
            if (operator === 'equal') {
              return `${fieldWithRepeated} = ${value}`;
            }
            if (operator === 'empty') {
              return `${fieldWithRepeated} = null`;
            }
            if (operator === 'notEmpty') {
              return `${fieldWithRepeated} != null`;
            }
            return '';
          })
          .join(` ${operator.toUpperCase()} `)})`;
      }
      if (fieldType === FieldTypeEnum[FieldType.Boolean]) {
        return `(${filters[fieldNoQuote].map(v => `${field} = ${v}`).join(' OR ')})`;
      }
      const filtersWithOutNull = filters[fieldNoQuote]
        .filter(v => v)
        .map(mapValueFunc)
        .map(v => `${prefixes[fieldType] || ''}${v}`);
      const hasNull = filters[fieldNoQuote].length > filtersWithOutNull.length;
      const result = `(${[
        filtersWithOutNull.length ? `${field} in(${filtersWithOutNull.join(', ')})` : '',
        hasNull ? `${field} = null` : '',
      ]
        .filter(d => d)
        .join(' OR ')})`;
      return result;
    }),
  ];

  if (filters.search?.[0]) {
    whereParts.push(
      `(${(entityViewConfig[entityTypeId] as EntityConfigType).searchConfig.fields
        ?.map(v => `${v} LIKE '%${getEscapedValue(filters.search[0])}%'`)
        .join(' OR ')})`
    );
  }

  if (filters.searchArray?.length && filters.searchArray[0]) {
    whereParts.push(
      `(${filters.searchArray
        .slice(1)
        .map(v => `${v} LIKE '%${getEscapedValue(filters.searchArray[0])}%'`)
        .join(' OR ')})`
    );
  }
  if (extra) {
    const extraArr = Array.isArray(extra) ? extra : [extra];
    whereParts.push(...extraArr.filter(f => f));
  }
  return whereParts.length ? `where ${whereParts.join(' AND ')}` : '';
};

export function useExportSqlToCsv(sql, headers, origin, exportData: unknown = undefined) {
  const { enqueueSnackbar } = useSnackbar();
  const [isWorking, setIsWorking] = useState<number | undefined>();
  const exportToCSV = data => {
    const filteredHeaders = headers.filter(h => !!(h.label || h.header));
    const csv = arrayToCsv([
      filteredHeaders.map(h => h.label || h.header),
      ...data.map(row =>
        filteredHeaders.reduce((acc, { id: key, getValue }) => {
          acc.push(getValue ? getValue(row) : typeof row[key] === 'object' ? JSON.stringify(row[key]) : row[key]);
          return acc;
        }, [])
      ),
    ]);
    return downloadBlob(csv, 'text/csv;charset=utf-8;', `export-${Date.now()}.csv`);
  };
  const { isLoading } = useQuerySql({
    origin,
    key: `${isWorking}-sql`,
    sql: isWorking ? sql : null,
    debounceQueries: false,
    options: { enabled: !exportData },
    onSuccess: d => {
      try {
        exportToCSV(d);
        setIsWorking(undefined);
      } catch (e) {
        enqueueSnackbar('Export failed', { variant: 'error' });
        console.error(e);
      }
    },
  });

  return { exportToCSV: () => (exportData ? exportToCSV(exportData) : setIsWorking(Date.now())), isLoading };
}

export function useExportQueryObjectToCsv(queryObject, headers, origin, globalFilters) {
  const { enqueueSnackbar } = useSnackbar();
  const [isWorking, setIsWorking] = useState(false);
  const exportToCSV = data => {
    const filteredHeaders =
      headers?.filter(h => !!h.label) ||
      Object.keys(data[0]).reduce<{ id: string; label: string }[]>(
        (acc, key) => (key !== 'id' ? [...acc, { id: key, label: key }] : acc),
        []
      );
    const csv = arrayToCsv([
      filteredHeaders.map(h => h.label),
      ...data.map(row =>
        filteredHeaders.reduce((acc, { id: key, getValue }) => {
          acc.push(getValue ? getValue(row) : row[key]);
          return acc;
        }, [])
      ),
    ]);
    return downloadBlob(csv, 'text/csv;charset=utf-8;', `export-${Date.now()}.csv`);
  };

  const { data, isLoading } = useQueryObject({
    origin,
    queryObject,
    options: { enabled: isWorking },
    contextFilter: globalFilters,
    onSuccess: data => {
      try {
        exportToCSV(data);
      } catch (e) {
        enqueueSnackbar('Export failed', { variant: 'error' });
        console.error(e);
      }
      setIsWorking(false);
      return data;
    },
  });
  useEffect(() => {
    if (data && isWorking) {
      exportToCSV(data);
      setIsWorking(false);
    }
  }, [isWorking]);

  return { exportToCSV: () => setIsWorking(true), isLoading };
}

export const TicketInfoContext = createContext<Record<string, any>>({});
export const useTicketInfo = () => useContext(TicketInfoContext);

export const buildOutegrationsQuery = outegrations =>
  outegrations
    ?.filter(
      ({ matchingConditionAsSql, targetMatchingType }) =>
        matchingConditionAsSql && targetMatchingType === TARGET_MATCHING_TYPE_ENUM.CONDITION
    )
    .map(alert => `IF(${alert?.matchingConditionAsSql}, true, false) as target_${alert?.id}`)
    .join(',');

export interface UserView {
  id: string;
  name: string;
  global: boolean;
  config: { [key: string]: any };
  accountDefaultView?: boolean;
  defaultView?: boolean;
  createdByUserId?: string;
}

export const useUserViews = ({
  screenType,
  proj,
  enabled = true,
}: {
  screenType: ScreenType | undefined;
  proj?: { name: string; builtIn: boolean };
  enabled?: boolean;
}) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();
  const {
    data: userViews,
    isLoading,
    refetch,
  } = useGqlQuery<UserView[]>({
    queryKey: [QueryKey.Views, screenType, ...(proj ? [proj.name] : [])],
    query: SAVED_VIEWS,
    variables: { screenType, ...(proj && { projectionId: proj }) },
    enabled: enabled && !!screenType && hasAllowedPermissionToResource({ resource: PermissionEntitiesNames.VIEWS }),
  });

  return { userViews: userViews || [], isLoading, refetch };
};

export const getHeadCellsWithSort = (headCells, orderBy) =>
  headCells.map(h => {
    const orderByItem = orderBy.find(o => o.property === h.id);
    const sort = orderByItem ? (orderByItem.isAsc ? 'asc' : 'desc') : undefined;
    return { ...h, sort };
  });

export const useBulkIntegration = (projectionId, selectedKeys, onSuccess) => {
  const { api } = useAvContext();
  return useMutation({
    mutationFn: outegrationId =>
      api(apiUrls.UPDATE_INTEGRATION, {
        options: { method: 'PUT' },
        isWebserver: false,
        body: { keys: selectedKeys, alertDestinationId: outegrationId, projectionId },
        onSuccess: onSuccess(outegrationId),
      }),
  });
};

export const useBulkSinglesIntegration = ({ onSuccess }) => {
  const { api } = useAvContext();
  return useMutation({
    mutationFn: ({ request }: { request: IntegrationSingleTM }) =>
      api(CREATE_BULK_SINGLES_INTEGRATION, {
        options: { request },
        onSuccess: ({ data }) => {
          onSuccess('')(data.bulkCreateIntegration);
        },
      }),
  });
};

export const useGetFile = ({
  url,
  fileId,
  isPreview,
  enabled = true,
  setGetFileError,
}: {
  url: string;
  fileId: string;
  isPreview: boolean;
  enabled?: boolean;
  setGetFileError?: (errors: any) => void;
}) => {
  const { api, featureFlags } = useAvContext();
  const handleError = useHandleError(featureFlags[FeatureFlags.ShowServerErrorInToast]);

  return useQuery({
    queryKey: [`${fileId}${isPreview}`],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(`${url}&preview=${isPreview}&storedName=${fileId}`, {
        isWebserver: false,
        isJson: false,
        muteErrors: true,
        signal,
        onSuccess: async data => {
          const blob = await data.blob();
          return { dataURL: URL.createObjectURL(blob), blob };
        },
      }).catch(errors => {
        if (getNetworkErrorCode(errors) === 422) {
          console.warn(errors);
          setGetFileError?.(errors);
        } else {
          handleError(errors);
        }
        return undefined;
      }),
    enabled: !!fileId && enabled,
  });
};

export const useGetRelatedTickets = ({ ticket, headCells }) => {
  const isThreatsApp = useIsThreatsApp(); // remove when entity view config will be by projection + app name
  const { entityType, projectionId, ticketKey, entityPathAlias, entityViewConfigKey } = ticket;
  const { groupingKeyField } = entityViewConfig[isThreatsApp ? entityViewConfigKey : entityType];
  const { data: groupingKeys, isLoading } = useQuerySql({
    key: 'grouping_keys',
    sql: `SELECT DISTINCT ${groupingKeyField} FROM ${projectionId.name} WHERE ${entityPathAlias}._key = '${ticketKey}'`,
    options: {
      enabled: !!groupingKeyField,
    },
    debounceQueries: false,
    onSuccess: data =>
      data
        .map(({ [groupingKeyField as string]: groupingKey }) => groupingKey && `'${getEscapedValue(groupingKey)}'`)
        .filter(d => d)
        .join(','),
  });
  const relatedTicketsWhereClause = groupingKeyField
    ? `WHERE ${groupingKeyField} in (${groupingKeys}) AND ${entityPathAlias}._key != '${ticketKey}'`
    : '';
  const baseEntitySqlsMap = useMemo(() => getBaseEntitySqlsMap(isThreatsApp), []);
  const baseSQL = baseEntitySqlsMap[entityType]({ fields: headCells, where: relatedTicketsWhereClause });
  const result = useQuerySql({
    key: 'related_tickets',
    sql: baseSQL,
    debounceQueries: false,
    options: {
      enabled: !!(groupingKeys?.length && groupingKeyField),
    },
  });
  return { ...result, isLoading: isLoading || result.isLoading };
};

const getGroupByActionsMap = entityTypeId => ({
  [groupByKeys.assignee?.[entityTypeId]]: { sort: groupByKeys.assignee.sort },
  [groupByKeys.severity?.[entityTypeId]]: { sort: groupByKeys.severity.sort, format: groupByKeys.severity.formatter },
  [groupByKeys.sla?.[entityTypeId]]: { sort: groupByKeys.sla.sort, format: groupByKeys.sla.formatter },
  [groupByKeys.firstSeen?.[entityTypeId]]: { sort: groupByKeys.firstSeen.sort, format: groupByKeys.firstSeen.formatter },
  [groupByKeys.avalorStatus?.[entityTypeId]]: { sort: groupByKeys.avalorStatus.sort, format: groupByKeys.avalorStatus.formatter },
  [groupByKeys.created?.[entityTypeId]]: { sort: groupByKeys.created.sort, format: groupByKeys.created.formatter },
  [groupByKeys.type?.[entityTypeId]]: { sort: groupByKeys.type.sort },
  'exception.status': {
    sort: ({ title: a }, { title: b }) => (a === 'Requested' ? -1 : b === 'Requested' ? 1 : a.localeCompare(b)),
  },
});

export const LIMIT_GROUP_BY_VALUES = 40000;
export const useGetGroupBy = ({
  groupByKey,
  entityTypeId,
  entityName,
  projectionName,
  projections,
  where,
  onSuccess,
  displayNameMapFormatter,
  enabled = false,
}: {
  groupByKey: string;
  entityTypeId: string;
  entityName: string;
  projectionName: string;
  projections: AccountEntities;
  where: string;
  onSuccess?: (options: any[]) => void;
  displayNameMapFormatter?: { [key: string]: (value: any) => string };
  enabled?: boolean;
}) => {
  const { fieldMap } = useAvContext().accountEntities;

  const isKeyDateField = [FieldType.DateTime, FieldType.Date].includes(fieldMap[groupByKey]?.type as any);
  const groupByValuesSql = sqlFormatter(
    `SELECT ${
      groupByKeysQueryProjection(entityTypeId)[groupByKey] || `${groupByKey} as val`
    }, COUNT_DISTINCT(${entityName}._key) as count from ${projectionName} ${where} GROUP BY val ORDER BY val ASC${
      isKeyDateField ? ' LIMIT 1000' : ` LIMIT ${LIMIT_GROUP_BY_VALUES}`
    }`
  );
  const { data, refetch, isLoading } = useQuerySql({
    key: 'group_by_values',
    sql: groupByValuesSql,
    debounceQueries: false,
    options: { enabled: !!groupByKey && enabled },
    onSuccess: groupByValues => {
      const groupByKeyParts = groupByKey?.split('.');
      const keyToRetrieve = groupByKeyParts && groupByKeyParts.length > 1 ? groupByKeyParts.slice(1).join('.') : groupByKey;
      const groupByKeyField =
        projections[projectionName].nameMap[keyToRetrieve] ||
        projections[projectionName].fieldList.INTERACTIVE.find(({ pathAlias }) => pathAlias === keyToRetrieve)?.title;
      const optionsMap = groupByValues
        ? groupByValues.reduce((acc, row) => {
            const keys = Array.isArray(row.val) ? row.val : [row.val];
            keys.forEach(rawKey => {
              const title = `${groupByKeyField}: ${
                getGroupByActionsMap(entityTypeId)?.[groupByKey]?.format?.(rawKey) ||
                displayNameMapFormatter?.[groupByKey]?.(rawKey) ||
                rawKey
              }`;

              acc[rawKey] = { title, value: rawKey, count: (acc[rawKey]?.count || 0) + row.count };
            });
            return acc;
          }, {})
        : {};
      const options = Object.values(optionsMap);
      options.sort(getGroupByActionsMap(entityTypeId)?.[groupByKey]?.sort);
      onSuccess?.(options);
      return options;
    },
  });
  return { data, refetch, isLoading };
};

export const useGetShouldShowTabs = ({
  tabs = [],
  ticketKey,
  projectionName,
  entityPathAlias,
}: {
  tabs: TTab[];
  ticketKey: string;
  projectionName: string;
  entityPathAlias: string;
}) => {
  const dims = tabs.reduce<{ name: string; alias: string }[]>((acc, { name, shouldShowTabField }) => {
    if (shouldShowTabField) {
      const nameWithoutSpaces = name.replaceAll(' ', '');
      const fieldPathAlias = shouldShowTabField.split('.')[0];
      const fieldExpression = `IF(COUNT_DISTINCT_IF(${fieldPathAlias}._key, ${shouldShowTabField} != null) > 0, true, false)`;
      return [...acc, { name: fieldExpression, alias: nameWithoutSpaces }];
    }
    return acc;
  }, []);

  return useQueryObject({
    queryObject: {
      select: { dims, metrics: [] },
      projectionId: { name: projectionName, builtIn: true },
      filter: field(`${entityPathAlias}._key`).str.equals(getEscapedValue(ticketKey)),
      sorting: [],
    },
    onSuccess: data => data?.[0] || {},
    options: {
      enabled: !!dims.length && !!ticketKey,
    },
  });
};

export const useCreateIncident = onCreateIncidentSuccess => {
  const { api } = useAvContext();
  return useMutation({
    mutationFn: (alertKeys: string[]) =>
      api(CREATE_INCIDENT, {
        options: { alertKeys },
        onSuccess: ({ data }) => onCreateIncidentSuccess(data?.createManualIncident?.[0]),
      }),
  });
};

export const useGetSourcesValues = ({ projectionId, fieldName, entityPathAlias, avalorKey, field, showAll, projAliasPath }) => {
  const defaultValue = { sources: [], totalCount: 0 };

  const { data: { sources, totalCount } = defaultValue, isLoading } = useQueryObject({
    onSuccess: d =>
      d?.[0]
        ? {
            ...d?.[0],
            sources: d?.[0].sources
              .map(({ value, items }) => ({ value, source: items[0] }))
              .sort((a, b) => a.source.localeCompare(b.source)),
          }
        : defaultValue,
    options: { enabled: !!field.ingressSources, placeholderData: keepPreviousData },
    queryObject: generateSourcesValuesQuery({
      projectionId,
      fieldName,
      entityPathAlias,
      projAliasPath,
      avalorKey,
      sources: field.ingressSources,
      showAll,
    }),
  });
  return { sources, totalCount, isLoading };
};

const getInstantLoadingMessage = () => [{ id: `${Date.now()}`, type: MessageStatus.LOADING, header: '', message: 'Loading' }];
export const useSplitEntities = ({ onSuccess = d => d }) => {
  const { api } = useAvContext();
  const { setInstantMessages, setMessageList } = useContext(NotificationContext);
  const instantMessage = getInstantLoadingMessage();

  return useMutation({
    mutationFn: ({
      name,
      sourceProjectionID,
      sourceProjectionKeys,
      splitStrategy = 'ALL',
      queryWhereClause = null,
      operation = 'SPLIT',
      overrideFields,
    }: {
      name: string;
      sourceProjectionID: EntityTypeID;
      sourceProjectionKeys?: string[];
      splitStrategy: string;
      queryWhereClause?: null | string;
      operation?: string;
      overrideFields?: Record<string, any>;
    }) => {
      setInstantMessages(instantMessage);
      return api(SPLIT_ENTITIES, {
        options: {
          splitEntitiesRequest: {
            entityName: name,
            sourceProjectionID,
            sourceProjectionKeys,
            splitStrategy,
            queryWhereClause,
            operation,
            ...(overrideFields ? { overrideFields } : {}),
          },
        },
        onSuccess: ({ data }) => {
          setMessageList(prev => prev.filter(message => message.id !== instantMessage[0].id));
          return onSuccess(data.entitiesSplit);
        },
      });
    },
  });
};

export const useMergeEntities = ({ onSuccess = d => d }) => {
  const { api } = useAvContext();
  const { setInstantMessages, setMessageList } = useContext(NotificationContext);
  const instantMessage = getInstantLoadingMessage();
  return useMutation({
    mutationFn: ({
      title,
      keys,
      projectionId,
      overrideFields = {},
    }: {
      title: string;
      keys: string[];
      projectionId: EntityTypeID;
      overrideFields: Record<string, any>;
    }) => {
      setInstantMessages(instantMessage);
      return api(MERGE_ENTITIES, {
        options: {
          mergeEntitiesRequest: {
            keys,
            projectionID: projectionId,
            overrideFields: {
              title,
              ...overrideFields,
            },
          },
        },
        onSuccess: ({ data }) => {
          setMessageList(prev => prev.filter(message => message.id !== instantMessage[0].id));
          return onSuccess(data.entitiesMerge);
        },
      });
    },
  });
};

export const useGetEntityActions = (projectionName, ignorePermissions = false) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();
  const entityConfig = Object.values(entityViewConfig).find(({ projectionName: p }) => p === projectionName);
  const actionsMap = Object.values(entityConfig?.actions || {}).reduce((acc, curr) => ({ ...acc, ...curr }), {});
  return Object.keys(actionsMap).reduce((acc, act) => {
    if (
      ignorePermissions
        ? !!actionsMap[act]
        : !!actionsMap[act] &&
          (getActionsPermissionConfig(projectionName)[act]
            ? hasAllowedPermissionToResource({
                category: projectionNameToCategoryName[projectionName] as PermissionCategoriesNames,
                ...getActionsPermissionConfig(projectionName)[act],
              })
            : true)
    ) {
      return { ...acc, [act]: true };
    }
    return { ...acc, [act]: false };
  }, {});
};

export const useGetAllFields = ({ allFields, entityPathAlias, ticketKey, projectionId, showAllFields }) => {
  const queryObject: DwQueryRequest = {
    select: {
      dims: allFields.map(v => ({ name: v.value })),
      metrics: [],
    },
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: `${entityPathAlias}._key`,
              stringCondition: {
                equals: ticketKey,
              },
            },
          },
        ],
      },
    },
    projectionId,
    sorting: [],
  };
  const { data: allFieldsData = emptyObject, isLoading: isLoadingAllFieldsData } = useQueryObject({
    queryObject,
    onSuccess: d => d[0],
    options: { enabled: showAllFields && !!ticketKey },
  });
  return { allFieldsData, isLoadingAllFieldsData };
};

export const useGetTicketMetricsData = ({
  projName,
  entityPathAlias,
  ticketKey,
}: {
  projName: string;
  entityPathAlias: string;
  ticketKey?: string | number;
}) => {
  const { data: metricsData, isLoading: isLoadingMetrics } = useQueryObject({
    queryObject: {
      select: {
        metrics: metricsPerProjName[projName],
        dims: [],
      },
      filter: field(`${entityPathAlias}._key`).str.equals(`${ticketKey}`),
      projectionId: { name: projName, builtIn: true },
      sorting: [],
    },
    options: { enabled: !!ticketKey && !!metricsPerProjName[projName]?.length },
  });
  return { ticketMetricsData: metricsData?.[0], isLoading: isLoadingMetrics };
};

export const useGetIsManualOverrideEnabled = ({ ticket, tabType }) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
    accountEntities: { fieldMap },
  } = useAvContext();
  const { data: { fieldRegulations } = emptyObject } = useFieldRegulationsData();
  const projectionName = ticket.projectionId.name;

  return (fieldName = '') => {
    const fieldProjection = fieldMap[fieldName]?.mainProjId?.name;
    const manualOverrideBaseCondition = fieldName =>
      hasAllowedPermissionToResource({
        category: projectionNameToCategoryName[fieldProjection || projectionName] as PermissionCategoriesNames,
        ...getActionsPermissionConfig(fieldProjection || projectionName)[ACTIONS_MAP.UPDATE],
      }) && getIsManualOverrideEnabled(getFieldName(fieldName), fieldProjection || projectionName, fieldRegulations);
    return ticket.projectionId.name === entityViewConfig.Finding.projectionName && tabType === TabTypeEnum.VULNERABILITY
      ? manualOverrideBaseCondition(getFieldName(fieldName))
      : ticket.activeTabProjection === fieldProjection && manualOverrideBaseCondition(getFieldName(fieldName));
  };
};

export const useGetFilteredFieldsList = ({
  sourceArray,
  allTicketFieldsData,
  isLoadingAllFieldsData,
  targetArrayOfArrays,
  search,
  ticket,
  tabType,
  showAllFields,
}: {
  sourceArray: FieldListObject[];
  isLoadingAllFieldsData: boolean;
  showAllFields: boolean;
  allTicketFieldsData: Record<string, any>;
  targetArrayOfArrays: any[][];
  search: string;
  ticket: any;
  tabType: string;
}) => {
  const {
    accountEntities: { aggProjs },
  } = useAvContext();

  const isManualOverrideEnabled = useGetIsManualOverrideEnabled({ ticket, tabType });

  const projection = ticket.projectionId.name;

  const allFields = useMemo(
    () =>
      sourceArray.map(v => ({
        field: v.value,
        displayName: v.mainProjId.name === projection && v.value?.split('.')[0] === ticket.entityPathAlias ? v.displayName : v.title,
        isLoading: isLoadingAllFieldsData,
        group: v.group,
      })),
    [sourceArray, projection, isLoadingAllFieldsData, showAllFields]
  );

  const showAllFieldsList = [...targetArrayOfArrays.flat(), ...allFields]
    .map(v => {
      const fieldType = aggProjs[projection].typeMap[v.field];
      const isDate = [FieldType.DateTime, FieldType.Date].includes(fieldType as string);
      const fieldValue = isDate
        ? allTicketFieldsData[v.field]
          ? formatAsUTC(allTicketFieldsData[v.field], false, true)
          : 'Not Set'
        : v.showNull && !allTicketFieldsData[v.field]
          ? 'Empty'
          : allTicketFieldsData[v.field];

      return {
        ...v,
        isManualOverrideEnabled: isManualOverrideEnabled(v.field),
        fieldType,
        fieldValue,
      };
    })
    .filter(v => {
      const lowerSearch = search.toLowerCase();
      return (
        (v.displayName.toLowerCase().includes(lowerSearch) || JSON.stringify(v.fieldValue)?.toLowerCase()?.includes(lowerSearch)) &&
        (v.isManualOverrideEnabled || v.showNull || !!v.fieldValue)
      );
    })
    .sort((a, b) => (!a.group ? -1 : !b.group ? 1 : (a.group || '').localeCompare(b.group || '')))
    .reduce(
      (acc, field, index) => {
        const bucketIndex = index % 2;
        if (bucketIndex === 0) {
          return [[...acc[bucketIndex], field], acc[1]];
        }
        return [acc[0], [...acc[bucketIndex], field]];
      },
      [[], []] as ConfigField[][]
    );

  const showDefaultFields = targetArrayOfArrays.map(array =>
    array.map(v => ({
      ...v,
      isManualOverrideEnabled: isManualOverrideEnabled(v.field),
      fieldType: aggProjs[projection].typeMap[v.field],
    }))
  );

  return showAllFields ? showAllFieldsList : showDefaultFields;
};

const SPLIT_ENTITIES = gql`
  mutation entitiesSplit($splitEntitiesRequest: SplitEntitiesRequest!) {
    entitiesSplit(splitEntitiesRequest: $splitEntitiesRequest) {
      wfId
      runId
    }
  }
`;

const MERGE_ENTITIES = gql`
  mutation entitiesMerge($mergeEntitiesRequest: MergeEntitiesRequest!) {
    entitiesMerge(mergeEntitiesRequest: $mergeEntitiesRequest) {
      wfId
      runId
    }
  }
`;

const SAVED_VIEWS = gql`
  query findViewsByUserIdAndAccountIdAndScreenType($screenType: ScreenType!, $projectionId: ProjectionIDInput) {
    findViewsByUserIdAndAccountIdAndScreenType(screenType: $screenType, projectionId: $projectionId) {
      id
      name
      screenType
      config
      publicView
      createdByUserId
      createdAt
      favorite
      defaultView
      global
      accountDefaultView
    }
  }
`;

const ALERT_DESTINATIONS_LIST_BY_PROJECTION_NAME = gql`
  query findIntegrationInstanceByAccountIdAndProjectionName($projectionName: String!) {
    findIntegrationInstanceByAccountIdAndProjectionName(projectionName: $projectionName) {
      id
      name
      active
      integrationType
      matchingConditionAsSql
      targetMatchingType
      category
      redactedConfig
      projectionName
      redactedAuthConfig
      mapping {
        createMapping
        mappingProjection
      }
    }
  }
`;

const CREATE_INCIDENT = gql`
  mutation createManualIncident($alertKeys: [String]!) {
    createManualIncident(alertKeys: $alertKeys) {
      wfId
      runId
    }
  }
`;

const CREATE_BULK_SINGLES_INTEGRATION = gql`
  mutation bulkCreateIntegration($request: BulkCreateIntegrationRequestInput!) {
    bulkCreateIntegration(request: $request) {
      wfId
      runId
    }
  }
`;

export const ALL_ALERT_DESTINATIONS_LIST_BY_PROJECTION_NAME = gql`
  query findIntegrationInstanceByAccountIdAndProjectionName($projectionName: String!) {
    findIntegrationInstanceByAccountIdAndProjectionName(projectionName: $projectionName) {
      id
      name
      active
      integrationType
    }
  }
`;
